import type { PendingSchedule } from '@/controller/worksheetAssignSchedule.controller'
import { worksheetAssignScheduleController } from '@/controller/worksheetAssignSchedule.controller'
import type { Worksheet } from '@/dto'
import commonRepo from '@/repository/common.repo'
import type { FixOption } from '@/types/makeWorksheet'

import { SCHEDULE_STRINGS } from '../_constants/scheduleStrings'
import { confirmWarning } from './scheduleConfirm'
import { toastScheduleError } from './scheduleError'

type Input = {
  worksheetId: Worksheet['id']
  fixOption: FixOption
  hasAssignedStudents: boolean
}

/**
 * 스쿨플랫에서 수정(EDIT·EDIT_ASSIGNED)과 수정 후 재출제(COPY + 출제 학생)만 대상이다.
 * 복제 후 수정은 원본을 건드리지 않고, 출제 학생이 있는 EDIT 는 뒤에서 "수정 불가" 로 막히므로 예약을 건드리지 않는다
 */
const isGuardTarget = ({ fixOption, hasAssignedStudents }: Omit<Input, 'worksheetId'>) => {
  if (!commonRepo.isSchoolflat) return false
  if (fixOption === 'EDIT') return !hasAssignedStudents
  if (fixOption === 'EDIT_ASSIGNED') return true
  return fixOption === 'COPY' && hasAssignedStudents
}

/**
 * 수정 화면으로 넘어가기 전에 그 학습지의 대기 예약을 확인하고 묻는다.
 * - false: 여기서 멈춘다 (교사가 취소했거나 조회 실패)
 * - null: 대기 예약이 없어 그냥 진행한다
 * - PendingSchedule: 교사가 확인했다. 호출부는 화면 이동 직전에 cancelPendingScheduleForEdit 로 취소한다
 *   (수정 준비가 실패하면 예약을 그대로 두기 위해 취소를 뒤로 미룬다)
 */
export const confirmPendingScheduleEdit = async ({
  worksheetId,
  ...target
}: Input): Promise<PendingSchedule | null | false> => {
  if (!isGuardTarget(target)) return null

  let pending
  try {
    pending = await worksheetAssignScheduleController.getPendingSchedule(worksheetId)
  } catch (error) {
    toastScheduleError(error)
    return false
  }
  if (!pending) return null

  return (await confirmWarning(SCHEDULE_STRINGS.editGuard())) ? pending : false
}

/** 얼럿에서 안내한 대로 예약을 취소한다. 실패하면 토스트 후 false 를 돌려 수정 화면으로 넘어가지 않게 한다 */
export const cancelPendingScheduleForEdit = async (pending: PendingSchedule) => {
  try {
    await worksheetAssignScheduleController.cancel(pending.batchId)
    return true
  } catch (error) {
    toastScheduleError(error)
    return false
  }
}
